import { useEffect, useState } from 'react';

import {
	TRAITS_FETCH_TIMEOUT_MS,
	TRAIT_HAS_SENT_ROVO_CHAT_MESSAGE,
	TRAIT_IS_ROVO_CHAT_MAU,
} from '../../common/constants';

type TraitValue = string | number | boolean | null;

type Trait = {
	name: string;
	value: TraitValue;
};

type PersonalizationResponse = {
	attributes?: Trait[];
};

const getUserTraitsUrl = (userId: string): string =>
	`/gateway/api/tap-delivery/api/v3/personalization/user/${encodeURIComponent(userId)}`;

async function fetchUserTraits(userId: string, signal: AbortSignal): Promise<Trait[]> {
	const response = await fetch(getUserTraitsUrl(userId), {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			// MAU is tracked server-side; this client read should not count as activity.
			'x-atlassian-mau-ignore': 'true',
		},
		signal,
	});

	if (!response.ok) {
		throw new Error(`TAP request failed with status ${response.status}`);
	}

	const json = (await response.json()) as PersonalizationResponse;
	return json.attributes ?? [];
}

/**
 * Coerces a TAP trait value to a boolean. TAP may deliver booleans as real
 * booleans or as the strings `"true"`/`"false"`, so handle both. A missing trait
 * is treated as falsy.
 */
function isTraitTruthy(value: TraitValue | undefined): boolean {
	return value === true || value === 'true' || value === 1 || value === '1';
}

export type RovoChatEligibility = {
	/** True once the TAP fetch has settled (success or failure). */
	loaded: boolean;
	/**
	 * True when the user qualifies for the reintroduction modal: they have NOT
	 * sent a Rovo chat message OR they are dormant (not a Rovo chat MAU).
	 */
	eligible: boolean;
};

const DISABLED_RESULT: RovoChatEligibility = { loaded: false, eligible: false };

/**
 * Resolves client-side targeting for the Rovo reintroduction modal by reading
 * two TAP user-level traits and combining them with OR:
 *
 * - `segment_has_sent_rovo_chat_message_aaid` falsy → user has never sent a
 *   Rovo chat message.
 * - `is_rovo_chat_mau` falsy → user is dormant.
 *
 * If the fetch fails or times out, the user is treated as not eligible so a TAP
 * outage cannot show the modal to everyone.
 */
export function useRovoChatEligibility(
	userId: string | undefined,
	{ enabled = true }: { enabled?: boolean } = {},
): RovoChatEligibility {
	const [result, setResult] = useState<RovoChatEligibility>(DISABLED_RESULT);

	useEffect(() => {
		if (!enabled || !userId) {
			return;
		}

		let cancelled = false;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), TRAITS_FETCH_TIMEOUT_MS);

		fetchUserTraits(userId, controller.signal)
			.then((traits) => {
				if (cancelled) {
					return;
				}
				const findValue = (name: string): TraitValue | undefined =>
					traits.find((t) => t.name === name)?.value;
				const hasSentMessage = isTraitTruthy(findValue(TRAIT_HAS_SENT_ROVO_CHAT_MESSAGE));
				const isRovoChatMau = isTraitTruthy(findValue(TRAIT_IS_ROVO_CHAT_MAU));
				setResult({ loaded: true, eligible: !hasSentMessage || !isRovoChatMau });
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				// Network error or timeout abort: fail closed (do not show the modal).
				setResult({ loaded: true, eligible: false });
			})
			.finally(() => {
				clearTimeout(timeoutId);
			});

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
			controller.abort();
		};
	}, [userId, enabled]);

	return enabled ? result : DISABLED_RESULT;
}
